/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {reInitializeState} from '@ion/applications/src/helpers';
import {GeoFenceAttributes} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {BasemapTypes} from '@ion/features/src/maps/types/types';
import {PageState} from '@/types/pages';
import {AdminAction, AsyncState} from '@/types/redux';
import {
  ADD_ADMIN_GEOFENCES,
  ADD_ADMIN_GEOFENCES_ERROR,
  ADD_ADMIN_GEOFENCES_REQUEST,
  ADD_ADMIN_GEOFENCES_RESPONSE,
  ADMIN_GEOFENCES_GRID_REFRESHED,
  DELETE_ADMIN_GEOFENCE,
  DELETE_ADMIN_GEOFENCES,
  DELETE_ADMIN_GEOFENCES_ERROR,
  DELETE_ADMIN_GEOFENCES_REQUEST,
  DELETE_ADMIN_GEOFENCES_RESPONSE,
  EDIT_ADMIN_GEOFENCE,
  EDIT_ADMIN_GEOFENCES,
  EDIT_ADMIN_GEOFENCES_ERROR,
  EDIT_ADMIN_GEOFENCES_REQUEST,
  EDIT_ADMIN_GEOFENCES_RESPONSE,
  GET_ADMIN_GEOFENCES_ERROR,
  GET_ADMIN_GEOFENCES_REQUEST,
  GET_ADMIN_GEOFENCES_RESPONSE,
  INITIALIZE_ADMIN_GEOFENCES,
  RE_INITIALIZE_ADMIN_GEOFENCES,
  SET_ADMIN_GEOFENCES_GRID_FILTER_MODEL,
  SET_GRID_COLUMNS,
  SET_MAP_BASEMAP,
  SET_MAP_EXTENT,
  SET_MAP_INITIALIZED,
  SET_MAP_ZOOM,
  UPDATE_GRID_COLUMNS
} from '../actions/admin-geofences';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../helpers';

export interface AdminGeofenceActionState extends AsyncState {
  selected: GeoFenceAttributes | GeoFenceAttributes[] | null;
  result: GeoFenceAttributes | GeoFenceAttributes[] | null;
  isEditing: boolean;
  record?: GeoFenceAttributes | GeoFenceAttributes[] | null;
};
export interface AdminGeofencesState extends PageState {
  geofences: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {},
  },
  map: {
    initialized: boolean;
    basemap: BasemapTypes;
    extent: null;
    center: null;
    zoom: number;
  },
  add: AdminGeofenceActionState,
  edit: AdminGeofenceActionState,
  destroy: AdminGeofenceActionState,
  bulkEdit: AdminGeofenceActionState,
  bulkDelete: AdminGeofenceActionState
};

export interface AdminGeofencesAction extends AdminAction {
  geofence?: GeoFenceAttributes;
  geofences?: GeoFenceAttributes[];
};

// defines the user preference key for the admin groups page ...
const PREFERENCE = 'admin-geofences';

// defines the initial state for the reducer ...
export const initialState: AdminGeofencesState = {
  isInitialized : false,
  geofences     : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {}
  },
  map : {
    initialized : false,
    basemap     : BasemapTypes.grayVector,
    extent      : null,
    center      : null,
    zoom        : 6
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkEdit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkDelete : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    record    : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_GEOFENCES] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [RE_INITIALIZE_ADMIN_GEOFENCES] : draft => {
    reInitializeState(draft, {initialState});
  },
  [SET_GRID_COLUMNS] : ({grid, geofences}: AdminGeofencesState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : geofences.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminGeofencesState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [GET_ADMIN_GEOFENCES_REQUEST] : ({geofences}: AdminGeofencesState) => {
    geofences.loading = true;
  },
  [GET_ADMIN_GEOFENCES_RESPONSE] : ({geofences}: AdminGeofencesState) => {
    geofences.loading = false;
  },
  [GET_ADMIN_GEOFENCES_ERROR] : (
    {geofences}: AdminGeofencesState, {error}: AdminGeofencesAction
  ) => {
    geofences.loading = false;
    geofences.error   = error;
  },
  [SET_MAP_INITIALIZED] : ({map}: AdminGeofencesState) => {
    map.initialized = true;
  },
  [SET_MAP_BASEMAP] : ({map}: AdminGeofencesState, {basemap}) => {
    map.basemap = basemap;
  },
  [SET_MAP_INITIALIZED] : ({map}: AdminGeofencesState) => {
    map.initialized = true;
  },
  [SET_MAP_EXTENT] : ({map}: AdminGeofencesState, {extent}) => {
    map.extent = extent;
  },
  [SET_MAP_ZOOM] : ({map}: AdminGeofencesState, {zoom}) => {
    map.zoom = zoom;
  },
  [SET_ADMIN_GEOFENCES_GRID_FILTER_MODEL] : ({grid}: AdminGeofencesState, {model}) => {
    grid.filterModel = model;
  },
  [ADD_ADMIN_GEOFENCES] : ({add}: AdminGeofencesState, {show}: AdminGeofencesAction) => {
    add.isEditing = show;
    add.result    = null;
  },
  [ADD_ADMIN_GEOFENCES_REQUEST] : ({add}: AdminGeofencesState) => {
    add.loading = true;
    add.error   = add.result = null;
  },
  [ADD_ADMIN_GEOFENCES_RESPONSE] : ({add}: AdminGeofencesState, {geofence}: AdminGeofencesAction) => {
    add.loading   = false;
    add.isEditing = false;
    add.result    = geofence;
  },
  [ADD_ADMIN_GEOFENCES_ERROR] : ({add}: AdminGeofencesState, {error}: AdminGeofencesAction) => {
    add.loading   = false;
    add.error     = error;
  },
  [ADMIN_GEOFENCES_GRID_REFRESHED] : (
    {add, edit, destroy, bulkEdit, bulkDelete}: AdminGeofencesState
  ) => {
    add.result = null;
    edit.result = null;
    destroy.result = null;
    bulkEdit.result = null;
    bulkDelete.result = null;
  },
  [EDIT_ADMIN_GEOFENCE] : ({edit}: AdminGeofencesState, {geofence, show}: AdminGeofencesAction) => {
    edit.selected  = show ? geofence : null;
    edit.isEditing = show;
    edit.result    = null;
  },
  [EDIT_ADMIN_GEOFENCES] : ({bulkEdit}: AdminGeofencesState, {geofences, show}: AdminGeofencesAction) => {
    bulkEdit.selected  = show ? geofences : null;
    bulkEdit.isEditing = show;
    bulkEdit.result    = null;
  },
  [EDIT_ADMIN_GEOFENCES_REQUEST] : ({bulkEdit}: AdminGeofencesState) => {
    bulkEdit.loading = true;
    bulkEdit.error   = bulkEdit.result = null;
  },
  [EDIT_ADMIN_GEOFENCES_RESPONSE] : ({bulkEdit}: AdminGeofencesState, {geofences}: AdminGeofencesAction) => {
    bulkEdit.loading   = false;
    bulkEdit.isEditing = false;
    bulkEdit.result    = geofences;
  },
  [EDIT_ADMIN_GEOFENCES_ERROR] : ({bulkEdit}: AdminGeofencesState, {error}: AdminGeofencesAction) => {
    bulkEdit.loading   = false;
    bulkEdit.error     = error;
  },
  [DELETE_ADMIN_GEOFENCE] : ({destroy}: AdminGeofencesState, {geofence, show}: AdminGeofencesAction) => {
    destroy.selected  = show ? geofence : null;
    destroy.isEditing = show;
    destroy.result    = null;
  },
  [DELETE_ADMIN_GEOFENCES] : ({bulkDelete}: AdminGeofencesState, {geofences, show}: AdminGeofencesAction) => {
    bulkDelete.selected  = show ? geofences : null;
    bulkDelete.isEditing = show;
    bulkDelete.result    = null;
    bulkDelete.record    = null;
  },
  [DELETE_ADMIN_GEOFENCES_REQUEST] : ({bulkDelete}: AdminGeofencesState) => {
    bulkDelete.loading = true;
    bulkDelete.error   = bulkDelete.result = bulkDelete.record = null;
  },
  [DELETE_ADMIN_GEOFENCES_RESPONSE] : ({bulkDelete}: AdminGeofencesState, {geofences}: AdminGeofencesAction) => {
    bulkDelete.loading   = false;
    bulkDelete.isEditing = false;
    bulkDelete.result    = geofences;
    bulkDelete.record    = geofences;
  },
  [DELETE_ADMIN_GEOFENCES_ERROR] : ({bulkDelete}: AdminGeofencesState, {error}: AdminGeofencesAction) => {
    bulkDelete.loading = false;
    bulkDelete.error   = error;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminGeofencesState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
